import { noCase } from "change-case";
import { useEffect, useState } from "react";
// @mui
import {
  Box,
  Stack,
  List,
  Badge,
  Avatar,
  Divider,
  Typography,
  ListItemText,
  ListSubheader,
  ListItemAvatar,
  ListItemButton,
} from "@mui/material";
// utils
import { fToNow } from "../../../utils/formatTime";
// _mock_
// import { _notifications } from '../../../_mock/arrays';
// components
import Iconify from "../../../components/iconify";
import Scrollbar from "../../../components/scrollbar";
import MenuPopover from "../../../components/menu-popover";
import { IconButtonAnimate } from "../../../components/animate";
import { PRIMARY } from "../../../theme/palette";
import { SUBSCRIBE_FORMS, USER_FORMS_PENDING } from "../../../graphql/forms";
import { useQuery, useSubscription } from "@apollo/client";
import { useNavigate, useParams } from "react-router";
import { useAuthContext } from "../../../sections/auth";
import { useSnackbar } from "notistack";
import { Search } from "@mui/icons-material";

// ----------------------------------------------------------------------

export default function SignNotifications() {
  const { user } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();

  const [openPopover, setOpenPopover] = useState(null);
  const [count, setCount] = useState(0);
  const [listPending, setListPending] = useState();
  const {
    data: siteInspectionLists,
    loading: siteInspectionListsLoading,
    refetch,
  } = useQuery(USER_FORMS_PENDING, {
    skip: !user?.id,
    variables: {
      uid: user?.id,
    },
  });

  const {
    data: employeeSubs,
    loading: employeeSubsLoading,
    error: subscriptionError,
  } = useSubscription(SUBSCRIBE_FORMS, {
    variables: {
      uid: user?.id,
    },
    shouldResubscribe: true,
  });
  useEffect(() => {
    if (!subscriptionError && !employeeSubsLoading && employeeSubs) {
      refetch();
      enqueueSnackbar("New Form to Sign!");
    }
  }, [employeeSubs, employeeSubsLoading, subscriptionError]);

  useEffect(() => {
    if (
      !siteInspectionListsLoading &&
      siteInspectionLists?.formSigningRequestsList?.count
    ) {
      const userFormPendingCount =
        siteInspectionLists?.formSigningRequestsList?.count;

      setCount(userFormPendingCount);
      setListPending(siteInspectionLists?.formSigningRequestsList?.items);
    }
  }, [siteInspectionLists, siteInspectionListsLoading]);

  const handleOpenPopover = (event) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  return (
    <>
      <IconButtonAnimate
        color={openPopover ? "default" : "default"}
        onClick={handleOpenPopover}
        sx={{ width: 40, height: 40, background: `#fff !important` }}
      >
        <Badge color="error" badgeContent={count} max={100}>
          <Iconify
            icon="mdi:email"
            sx={{ width: 30, height: 30 }}
            color="#637381"
          />
        </Badge>
      </IconButtonAnimate>

      <MenuPopover
        open={openPopover}
        onClose={handleClosePopover}
        sx={{ width: 360, p: 0 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Form Notifications</Typography>
          </Box>
        </Box>

        <Divider sx={{ borderStyle: "dashed" }} />

        <Scrollbar sx={{ height: { xs: 340, sm: "auto" } }}>
          <List
            disablePadding
            subheader={
              !listPending && (
                <ListSubheader
                  disableSticky
                  sx={{
                    py: 1,
                    px: 2.5,
                    typography: "overline",
                    textAlign: "center",
                    padding: "2rem",
                  }}
                >
                  No New Forms to Sign
                </ListSubheader>
              )
            }
          >
            {listPending &&
              listPending.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                />
              ))}
            {listPending && (
              <ListItemButton
                sx={{
                  py: 1.5,
                  px: 2.5,
                  mt: "1px",
                  textAlign: "center",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Typography variant="subtitle1">
                  <Search /> View All Notifications
                </Typography>
              </ListItemButton>
            )}
          </List>
        </Scrollbar>
        <Divider sx={{ borderStyle: "dashed" }} />
      </MenuPopover>
    </>
  );
}

// ----------------------------------------------------------------------

function NotificationItem({ notification }) {
  const { avatar, title } = renderContent(notification);
  const navigate = useNavigate();

  return (
    <ListItemButton
      sx={{
        py: 1.5,
        px: 2.5,
        mt: "1px",
        ...(notification.status === "pending" && {
          bgcolor: "action.selected",
        }),
      }}
      onClick={() => {
        navigate(`/forms/${notification?.formSubmissions?.id}`);
      }}
    >
      <ListItemAvatar>
        <Avatar>{avatar}</Avatar>
      </ListItemAvatar>

      <ListItemText
        disableTypography
        primary={title}
        secondary={
          <>
            <Stack
              direction="row"
              sx={{ mt: 0.5, typography: "caption", color: "text.disabled" }}
            >
              <Typography variant="caption">ID: {notification.id}</Typography>
            </Stack>
            <Stack
              direction="row"
              sx={{ mt: 2, typography: "caption", color: "text.disabled" }}
            >
              <Iconify icon="eva:clock-fill" width={16} sx={{ mr: 0.5 }} />
              <Typography variant="caption">
                {fToNow(notification.createdAt)}
              </Typography>
            </Stack>
          </>
        }
      />
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function renderContent(notification) {
  const title = (
    <Typography variant="subtitle2">
      New Form to Sign
      <Typography
        component="span"
        variant="body2"
        sx={{ color: "text.secondary" }}
      >
        &nbsp;{" "}
        {noCase(
          `${notification?.createdBy?.firstName} ${notification?.createdBy?.lastName} assigned you a new Form to sign`
        )}
      </Typography>
    </Typography>
  );

  return {
    avatar: notification.avatar ? (
      <img
        alt={`New Form to Sign`}
        src="/assets/icons/notification/ic_mail.svg"
      />
    ) : null,
    title,
  };
}
